'use client';

import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { IProduct } from '@/entities/product/model/types';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { shallowEqual, useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import Nutrients from '../Nutrients';
import Availability from '../Availability';
import Rating from '../Rating';
import Description from '../Description';
import Ingredients from '../Ingredients';
import ProductDeliveryInfo from '../ProductDeliveryInfo';
import { Alert } from '@/components/ui/alert';
import { useTranslations } from 'next-intl';

interface ProductHeaderProps {
  data?: IProduct;
  isModal?: boolean;
}

export default function ProductHeader({ data, isModal }: ProductHeaderProps) {
  const currentLocale = useCustomLocale();
  const t = useTranslations();
  const { hide_product_reviews } = useSelector(
    (state: IRedux) => ({
      hide_product_reviews: state.settings.data?.data?.hide_product_reviews,
    }),
    shallowEqual,
  );

  return (
    <>
      <Box px={4}>
        <Text
          as='h1'
          fontSize={{ base: 'xl', lg: '3xl' }}
          lineHeight={1.2}
          fontWeight={600}
        >
          {data?.title?.[currentLocale]}
        </Text>
        <Stack gap={0} w='full' mt={{ base: 1, lg: 0 }}>
          {(data?.weight || 0) > 0 ? (
            <Stack mt={1} gap={1}>
              <Text as={'span'} color={'gray.700'} fontWeight={500}>
                {data?.weight} {data?.measurement || t('grams')}
              </Text>
              <Nutrients data={data} />
            </Stack>
          ) : null}

          {!hide_product_reviews && <Rating product={data} />}
          <HStack mt={5} flexWrap='wrap'>
            <Availability data={data} />
            <ProductDeliveryInfo isModal={isModal} />
          </HStack>
          <Description data={data} />
        </Stack>
      </Box>

      <Box px={6}>
        <Ingredients data={data} />
        {data?.allergens?.[currentLocale] && (
          <Alert
            mb={4}
            rounded={12}
            status='warning'
            title={data?.allergens?.[currentLocale]}
          />
        )}
      </Box>
    </>
  );
}
